import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import Dashboard from './components/Dashboard';
import ListTasks from './components/TaskList/ListTable';
import ChooseProducts from './components/ChooseProducts';
import ChooseFlypros from './components/ChooseFlypros';
import Product from './components/Product';
import "./App.css";
import Login from "./components/Login";
import ForgetPassword from "./components/ForgetPassword";
import Layout from "./components/Layout";
import RequireAuth from "./components/RequireAuth";
import RequireGuest from "./components/RequireGuest";
import PersistLogin from "./components/PersistLogin";
import RequireReset from "./components/RequireReset";
import Customer from "./components/Customer";
import Flypro from "./components/Flypro";
import Managers from "./components/User";
import Promotion from "./components/Promotion";
import Statistics from "./components/Statistics";
import Gift from "./components/Gift";
import Management from "./components/Management";
import Hotels from "./components/Hotels";
import Notification from "./components/Notification";
import Calendar from "./components/Calendar";
import ManagerProduct from "./components/HotelManager/Products";
import { useEffect, useState } from "react";
import { generateToken, messaging, isMessagingSupported } from "./utils/firebase";
import toast, { Toaster } from 'react-hot-toast';
import { onMessage } from "@firebase/messaging";
import NotificationAdmin from "./components/NotificationAdmin";
import { RoleBasedAccessControl } from "./components/RoleBasedAccessControl";
import BookingEditAdmin from './components/BookingEditAdmin';
import useAuth from "./hooks/useAuth";
import { initPusher, disconnectPusher } from "./services/pusher.service";
import useApi from "./hooks/useApi";
import { syncWebToken } from "./utils/webPushSync";

const ROLES = {
  Client: "ROLE_CLIENT",
  HotelManager: "ROLE_HOTEL_MANAGER",
  Admin: "ROLE_ADMIN",
  Manager: "ROLE_MANAGER",
};

const DashboardGate = () => {
  const { auth } = useAuth();

  if (!auth?.user) return null;

  if (auth.roles?.some(r => r.name === ROLES.HotelManager)) {
    return <Navigate to="/tasks" replace />;
  }

  return <Dashboard />;
};

function App() {
  console.log = function () {};
  console.info = function () {};
  console.debug = function () {};
  console.warn = function () {};
  console.error = function () {};
  useEffect(() => {
    const setupFirebaseMessaging = async () => {
      try {
        // Check if messaging is supported
        const isSupported = await isMessagingSupported();

        if (isSupported && messaging) {
          // Generate token only if messaging is supported
          const token = await generateToken();
          console.log("Firebase token generated:", token);

          // Set up message handler only if messaging is supported
          onMessage(messaging, (payload) => {
            console.log("Received message:", payload);
            if (payload.notification && payload.data) {
              toast(
                <a
                  href={payload.data.url}
                  rel="noopener noreferrer"
                  style={{
                    color: "#000000",
                    textDecoration: "underline",
                  }}
                >
                  {payload.notification.body}
                </a>
              );
            }
          });
        } else {
          console.log("Firebase messaging is not supported in this browser. Notifications will not be available.");
        }
      } catch (error) {
        console.error("Error setting up Firebase messaging:", error);
      }
    };

    setupFirebaseMessaging();
  }, []);
  const { auth } = useAuth();
  const api = useApi();
  useEffect(() => {
    initPusher({ token: auth?.accessToken });
    return () => {
      disconnectPusher();
    };
  }, [auth?.accessToken]);

  useEffect(() => {
    if (!auth?.user?.id) return;
    syncWebToken({ userId: auth.user.id, appName: "admin", axios: api }).catch(() => {});
  }, [auth?.user?.id, api]);
  return (
    <>
      {auth?.user &&
        <Toaster position="top-right" />
      }
      <Routes>
        <Route path="/" element={<Layout />}>

          <Route element={<PersistLogin />}>
            <Route element={<RequireGuest />}>
              <Route path="login" element={<Login />} />
              <Route path="forget_password" element={<ForgetPassword />} />
            </Route>
            <Route element={<RequireAuth ability={['admin.access']} />}>
              <Route element={<RequireAuth abilities={['bookings-read']} />}>
                <Route path="/" element={<DashboardGate />} />
              </Route>
              <Route element={<RequireAuth abilities={['bookings-manage', 'bookings-read']} />}>
                <Route path="/statistics" element={<Statistics />} />
              </Route>

            <Route element={<RequireAuth abilities={['users-manage.ROLE_CLIENT', 'users-read']} />}>
              <Route path="Customer" element={<Customer />} />
            </Route>

            <Route element={<RequireAuth abilities={['users-manage.ROLE_FLYPRO', 'users-read']} />}>
              <Route path="Flypro" element={<Flypro />} />
            </Route>

            <Route element={<RequireAuth abilities={['users-read', 'users-manage']} />}>
              <Route path="Managers" element={<Managers />} />
            </Route>

            <Route element={<RequireAuth abilities={['calendars-manage', 'calendars-read']} />}>
              <Route path="calendar" element={<Calendar />} />
            </Route>

            <Route element={<RequireAuth abilities={['hotels-manage', 'hotels-read']} />}>
              <Route path="Hotels" element={<Hotels />} />
            </Route>

            <Route element={<RequireAuth ability={['products-manage', 'products-read', 'domains-manage', 'domains-read', 'categories-manage', 'categories-read']} />}>
              <Route path="products" element={<Product />} />
              </Route>
            </Route>

            <Route element={<RequireAuth ability={['roles.manage', 'permissions-manage', 'roles-read', 'permissions-read']} />}>
              <Route path="rbac-management" element={<RoleBasedAccessControl />} />
            </Route>
            <Route element={<RequireAuth redirectPath="/" />} >
              <Route path="Management" element={<Management />} />
            </Route>
            <Route element={[<RequireAuth allowedRoles={[ROLES.Admin, ROLES.HotelManager]} />, <RequireReset />]}>
              <Route path="tasks" element={<ListTasks />} />
            </Route>
            <Route element={<RequireAuth allowedRoles={[ROLES.Admin, ROLES.HotelManager]} />}>
              <Route path="choose-products" element={<ChooseProducts />} />
              <Route path="choose-flypros" element={<ChooseFlypros />} />
            </Route>
            <Route element={<RequireAuth ability={['notifications-manage', 'notifications-read']} />}>
              <Route path="notification" element={<Notification />} />
              <Route path="notification-list" element={<NotificationAdmin />} />
            </Route>
            <Route element={<RequireAuth ability={['promotions-manage', 'promotions-read']} />}>
              <Route path="promotion" element={<Promotion />} />
            </Route>
            <Route element={<RequireAuth ability={['gift_configurations-manage', 'gift_configurations-read']} />}>
              <Route path="gift" element={<Gift />} />
            </Route>
            <Route element={<RequireAuth allowedRoles={[ROLES.HotelManager]} />}>
              <Route path="manager-products" element={<ManagerProduct />} />
              <Route path="/bookings/:bookingId/edit" element={<BookingEditAdmin />} />
            </Route>
            </Route>
          </Route>
      </Routes >
    </>
  );
}

export default App;
